import { useLazyQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  Rarity,
  So5FixtureEvent,
  type Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import type {
  GallerySortName,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import type { GQLCtx } from '@sorare/core/src/contexts/graphql-search';
import { keyFacetsByApiField } from '@sorare/core/src/contexts/graphql-search';
import { BACKEND_CARD_SEARCH_FILTERS } from '@sorare/core/src/contexts/graphql-search/backendCardSearchFilters.ts';
import { pickRenderedBackendFilters } from '@sorare/core/src/contexts/graphql-search/pickRenderedBackendFilters.ts';
import {
  type FilterConfig,
  useGQLSearch,
} from '@sorare/core/src/contexts/graphql-search/useGQLSearch.ts';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards/index.tsx';
import { useCompetitionEligibilityFilterValue } from '@sorare/core/src/contexts/searchCards/useCompetitionEligibilityFilterValue.ts';
import { useFavoriteFilterValue } from '@sorare/core/src/contexts/searchCards/useFavoriteFilterValue.ts';
import { useFixtureFilterValue } from '@sorare/core/src/contexts/searchCards/useFixtureFilterValue.ts';
import { useNotInLineupFilterValue } from '@sorare/core/src/contexts/searchCards/useNotInLineupFilterValue.ts';
import { useProbablePitchersFilterValue } from '@sorare/core/src/contexts/searchCards/useProbablePitchersFilterValue.ts';
import { useWatchlistFilterValue } from '@sorare/core/src/contexts/searchCards/useWatchlistFilterValue.ts';
import { useDeck } from '@sorare/core/src/hooks/decks/useDeck';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  joinFiltersWithAnd,
  joinFiltersWithOr,
  sportFilter,
  toFilterLiteral,
} from '@sorare/core/src/lib/algolia.ts';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';
import { toSortInputs } from '@sorare/marketplace/src/hooks/search/useCardsSearchClient';
import {
  USER_CARDS_SEARCH_FACET_VALUES_QUERY,
  USER_CARDS_SEARCH_QUERY,
} from '@sorare/marketplace/src/hooks/search/useUserCardsSearchClient';

type Params = {
  sport: Sport;
  rarity?: Rarity;
  cardFilters: FiltersGroup[];
  userSlug: string;
  cardSetSlug: Nullable<string>;
  commonSorts: GallerySortNames;
  blockchainSorts: GallerySortNames;
  revealCards?: boolean;
  defaultFilters?: string[];
};

export const useBackendMyCardsSearch = ({
  sport,
  rarity,
  cardFilters,
  userSlug,
  cardSetSlug,
  commonSorts,
  blockchainSorts,
  revealCards = false,
  defaultFilters = [],
}: Params) => {
  const activeBackendCardFilters = useMemo<FilterConfig>(() => {
    const picked = pickRenderedBackendFilters(
      BACKEND_CARD_SEARCH_FILTERS,
      cardFilters
    );
    if (!revealCards) return picked;
    // the reveal rarity switcher renders outside the cardFilters groups
    return { ...picked, rarity: { api: 'rarity', type: 'menu' } };
  }, [cardFilters, revealCards]);

  const { ctxBase, facets, searchParams } = useGQLSearch(
    activeBackendCardFilters
  );
  const { query, page, activeFacets, refinements, setSearchParams } = ctxBase;
  const [urlSearchParams] = useSearchParams();

  const sorts: GallerySortNames =
    rarity === Rarity.common ? commonSorts : blockchainSorts;
  const currentSortName = (searchParams.get('s') ??
    sorts[0]) as GallerySortName;

  const { filters: searchCardsFilters, sealed } = useSearchCardsContext();

  const notInLineupActive = !!activeFacets.nl?.length;
  const { notInLineupFilterValue, loading: notInLineupLoading } =
    useNotInLineupFilterValue({ skip: !notInLineupActive });

  const favoriteActive = !!activeFacets.ff?.length;
  const favoriteFilterValue = useFavoriteFilterValue({ skip: !favoriteActive });

  const competitionEligibility = activeFacets.lf?.[0];
  const competitionEligibilityFilterValue =
    useCompetitionEligibilityFilterValue(competitionEligibility);

  const collectibleActive = !!activeFacets.co?.length;

  const gameweekSlug = activeFacets.gw?.[0];
  const gameweekFilterValue = useFixtureFilterValue({
    filterValue: gameweekSlug || undefined,
    eventType: So5FixtureEvent.CLASSIC,
  });

  const dailySlug = activeFacets.dly?.[0];
  const dailyFilterValue = useFixtureFilterValue({
    filterValue: dailySlug || undefined,
    eventType: So5FixtureEvent.DAILY,
  });

  const watchlistId = activeFacets.wl?.[0];
  const { watchlistFilterValue, loading: watchlistLoading } =
    useWatchlistFilterValue({ filterValue: watchlistId || undefined });

  const probablePitchersValue = activeFacets.pp?.[0];
  const probablePitchersFilterValue = useProbablePitchersFilterValue({
    probablePitchersFilterValue: probablePitchersValue,
    skip: !probablePitchersValue,
  });

  const filtersLoading = notInLineupLoading || watchlistLoading;

  const deckName = activeFacets.deck?.[0];
  const { deck } = useDeck({ sport, name: deckName, skip: !deckName });
  const deckTokens = deck?.cards.nodes;
  const deckFilterValue = deckName
    ? joinFiltersWithOr([
        ...(deckTokens?.map(({ slug }) => `slug:"${toFilterLiteral(slug)}"`) ||
          []),
        `custom_decks.name:"${toFilterLiteral(deckName)}"`,
      ])
    : undefined;

  const advancedFilters = joinFiltersWithAnd(
    [
      ...defaultFilters,
      ...searchCardsFilters,
      ...(sport ? [sportFilter(sport)] : []),
      notInLineupActive && notInLineupFilterValue,
      favoriteActive && favoriteFilterValue,
      gameweekSlug && gameweekFilterValue,
      dailySlug && dailyFilterValue,
      competitionEligibility && competitionEligibilityFilterValue,
      !collectibleActive && 'NOT rarity:custom_series',
      watchlistId && watchlistFilterValue,
      probablePitchersValue && probablePitchersFilterValue,
      deckFilterValue,
    ].filter(Boolean) as string[]
  );

  const getDefaultRarity = () => {
    if (!rarity) return undefined;
    if (urlSearchParams.get(SEARCH_PARAMS.NON_PLAYABLE_CARDS) === 'true') {
      return Rarity.custom_series;
    }
    return rarity;
  };

  const duplicatedKey = activeFacets.dk?.[0] || undefined;

  const { data, loading, previousData, refetch } = useQuery(
    USER_CARDS_SEARCH_QUERY,
    {
      skip: !userSlug || filtersLoading,
      variables: {
        userSlug,
        rarity: revealCards ? undefined : getDefaultRarity(),
        sport,
        duplicatedKey,
        query,
        page,
        pageSize: 20,
        advancedFilters,
        sorts: toSortInputs(currentSortName, sport),
        refinements,
        facets,
        cardSetSlug,
      },
    }
  );

  const [search] = useLazyQuery(USER_CARDS_SEARCH_FACET_VALUES_QUERY, {
    variables: {
      userSlug,
      rarity: revealCards ? undefined : getDefaultRarity(),
      sport,
      query,
      advancedFilters,
      refinements,
      field: '',
      facetQuery: '',
      cardSetSlug,
    },
  });

  const safeData = data ?? previousData;
  const searchCards = safeData?.user?.searchCards;
  const hitSlugs = searchCards?.hits?.map(h => h.slug) ?? [];
  const nbHits = searchCards?.nbHits ?? 0;

  const gqlSearchContextValue: GQLCtx = {
    ...ctxBase,
    search,
    extractFacetValues: searchFacetData =>
      searchFacetData?.user?.searchCardFacetValues,
    pages: searchCards?.nbPages ?? 0,
    nbHits,
    advancedFilters,
    onSaleOnly: !!activeFacets.sale?.length,
    sorts: [currentSortName],
    facets: keyFacetsByApiField(searchCards?.facets ?? []),
  };

  return {
    gqlSearchContextValue,
    hitSlugs,
    loading,
    filtersLoading,
    nbHits,
    refetch,
    sorts,
    currentSortName,
    activeFacets,
    setSearchParams,
    sealed,
  };
};
